import { Product } from "@deliverr/commons-clients";
import { InboundPackageSummary } from "@deliverr/inbound-client";
import { ProductCasePack } from "common/clients/product/ProductCasePack";
import { CasePackDefaultData } from "common/clients/inbound/bulkUpload/CasePackDefaultData";
import { useCallback } from "react";
import { useDispatch, batch } from "react-redux";
import { CreateShipmentInputName } from "inbounds/createShipment/useCreateShipmentForm";
import { useCreateShipmentFormContext } from "inbounds/createShipment/useCreateShipmentFormContext";
import { setSelectedProductsCaseDimensions } from "../../../../dimensions/StorageCaseDimensionActions";
import { CaseDimensionCollection } from "../../../../dimensions/CaseDimensionCollection.types";
import { log } from "@deliverr/commons-logs";
import { BATCH_PROCESSING, chunkArray } from "common/constants/processing.const";

export type PackageWithCasePack = InboundPackageSummary & { casePackDsku: string };

const DEFAULT_PACKAGE_DIMENSIONS = {
  height: undefined,
  weight: undefined,
  length: undefined,
  width: undefined,
};

export const usePackageCreation = () => {
  const dispatch = useDispatch();
  const { register, setValue } = useCreateShipmentFormContext();

  const createDefaultPackage = useCallback((dsku: string, qty: number, caseQty: number): PackageWithCasePack => {
    const updatedCaseQty = caseQty ?? 1; // Prevent division by zero
    return {
      casePackDsku: "",
      numberOfPkgs: Math.ceil(qty / updatedCaseQty) ?? 1,
      items: [{ dsku, qty: updatedCaseQty }],
      ...DEFAULT_PACKAGE_DIMENSIONS,
    };
  }, []);

  const createPackageFromCaseDimension = useCallback(
    (
      dsku: string,
      casePack: ProductCasePack | null,
      caseDimension: CasePackDefaultData,
      product: Product & { qty: number }
    ): PackageWithCasePack => {
      const unitsPerBox = caseDimension.unitsPerCase ?? product.caseQty ?? 1;
      const updatedUnitsPerBox = unitsPerBox ?? 1; // Prevent division by zero
      const numberOfBoxes = Math.ceil(product.qty / updatedUnitsPerBox) ?? 1;

      return {
        casePackDsku: casePack?.dsku ?? "",
        numberOfPkgs: numberOfBoxes,
        items: [{ dsku, qty: updatedUnitsPerBox }],
        height: caseDimension.height,
        weight: caseDimension.weight,
        length: caseDimension.length,
        width: caseDimension.width,
      };
    },
    []
  );

  const createPackagesFromBulkUpload = useCallback(
    (
      product: Product & { qty: number },
      createdCasePacks: ProductCasePack[],
      caseDimensions: CasePackDefaultData[] = [],
      packages?: InboundPackageSummary[]
    ): PackageWithCasePack[] => {
      const { dsku, qty, caseQty = 1 } = product;
      const productCaseDimensions = caseDimensions.filter((cd) => cd.dsku === dsku);

      // Try to create packages from existing packages data
      if (packages?.length) {
        const productPackages = packages.filter(
          (pkg) => pkg.items?.length && pkg.items.length > 0 && pkg.items[0]?.dsku === dsku
        );

        if (productPackages.length) {
          return productPackages.map((pkg) => {
            const unitsPerBox = pkg.items?.[0]?.qty ?? 1;
            const matchingCasePack = createdCasePacks.find((casePack) => casePack?.quantity === unitsPerBox);

            return {
              casePackDsku: matchingCasePack?.dsku ?? "",
              numberOfPkgs: pkg.numberOfPkgs,
              items: pkg.items,
              height: pkg.height,
              weight: pkg.weight,
              length: pkg.length,
              width: pkg.width,
              sellerLPNs: pkg.sellerLPNs,
            };
          });
        }
      }

      // Create packages from case packs and dimensions
      if (createdCasePacks.length && productCaseDimensions.length) {
        return createdCasePacks.map((casePack, index) => {
          const caseDimension = productCaseDimensions[index];
          if (!caseDimension) {
            // Fallback to first available case dimension if index is out of bounds
            const fallbackDimension = productCaseDimensions[0];
            if (!fallbackDimension) {
              // If no dimensions available, create default package
              return createDefaultPackage(dsku, product.qty, product.caseQty ?? 1);
            }
            return createPackageFromCaseDimension(dsku, casePack, fallbackDimension, product);
          }
          return createPackageFromCaseDimension(dsku, casePack, caseDimension, product);
        });
      }

      // Create packages from case dimensions only
      if (productCaseDimensions.length) {
        return [createPackageFromCaseDimension(dsku, null, productCaseDimensions[0], product)];
      }

      // Final fallback: create default package
      return [createDefaultPackage(dsku, qty, caseQty)];
    },
    [createDefaultPackage, createPackageFromCaseDimension]
  );

  const createCaseDimensionsFromBulkUpload = useCallback(
    (caseDimensions: CasePackDefaultData[] = []): void => {
      if (!caseDimensions.length) {
        return;
      }

      try {
        // Process case dimensions in chunks to avoid blocking UI
        const dimensionChunks = chunkArray(caseDimensions, BATCH_PROCESSING.PROCESSING_CHUNK_SIZE);
        const productsContainerDimensions: CaseDimensionCollection = {};

        dimensionChunks.forEach((chunk) => {
          chunk.forEach(({ dsku, height, weight, length, width, dimensionUnit, weightUnit }) => {
            // Validate required fields
            if (!dsku) {
              log.warn("Skipping case dimension with missing DSKU");
              return;
            }

            productsContainerDimensions[dsku] = {
              height,
              weight,
              length,
              width,
              lengthUnit: dimensionUnit,
              weightUnit,
            };
          });
        });

        if (Object.keys(productsContainerDimensions).length > 0) {
          dispatch(setSelectedProductsCaseDimensions(productsContainerDimensions));
        }
      } catch (error) {
        log.error("Failed to process case dimensions:", error);
      }
    },
    [dispatch]
  );

  /**
   * Highly optimized bulk package creation with chunked processing
   * Target: <10 seconds for 1000 products
   */
  const createBulkPackagesFromBulkUpload = useCallback(
    (
      products: Array<Product & { qty: number }>,
      allProcessedCasePacks: Record<string, ProductCasePack[]>,
      caseDimensions: CasePackDefaultData[] = [],
      packages?: InboundPackageSummary[]
    ): Record<string, PackageWithCasePack[]> => {
      if (!products || products.length === 0) {
        log.warn("No products provided for package creation");
        return {};
      }

      const result: Record<string, PackageWithCasePack[]> = {};

      // Process products in chunks to avoid UI blocking and improve performance
      const productChunks = chunkArray(products, BATCH_PROCESSING.PROCESSING_CHUNK_SIZE);

      productChunks.forEach((productChunk, chunkIndex) => {
        productChunk.forEach((product) => {
          if (!product?.dsku) {
            log.warn("Skipping product with missing DSKU");
            return;
          }

          try {
            const createdCasePacks = allProcessedCasePacks[product.dsku] ?? [];
            const productPackages = createPackagesFromBulkUpload(product, createdCasePacks, caseDimensions, packages);

            if (productPackages && productPackages.length > 0) {
              result[product.dsku] = productPackages;
            } else {
              log.warn(`No packages created for product ${product.dsku}`);
              // Create default package as fallback
              result[product.dsku] = [createDefaultPackage(product.dsku, product.qty, product.caseQty ?? 1)];
            }
          } catch (error) {
            log.error(`Failed to create packages for product ${product.dsku}:`, error);
            // Create default package as fallback
            try {
              result[product.dsku] = [createDefaultPackage(product.dsku, product.qty, product.caseQty ?? 1)];
            } catch (fallbackError) {
              log.error(`Failed to create fallback package for ${product.dsku}:`, fallbackError);
            }
          }
        });
      });

      return result;
    },
    [createPackagesFromBulkUpload, createDefaultPackage]
  );

  /**
   * Optimized bulk package registration with better error handling
   * Registers all packages at once using batched Redux updates
   */
  const registerBulkPackages = useCallback(
    (allPackageData: Record<string, PackageWithCasePack[]>): void => {
      const registrationEntries = Object.entries(allPackageData);

      if (registrationEntries.length === 0) {
        log.warn("No packages to register");
        return;
      }

      try {
        // Process registration in optimized chunks for maximum performance
        const registrationChunks = chunkArray(registrationEntries, BATCH_PROCESSING.PACKAGE_REGISTRATION_BATCH_SIZE);

        registrationChunks.forEach((chunk, chunkIndex) => {
          // Use requestIdleCallback or setTimeout for non-blocking processing
          const processChunk = () => {
            batch(() => {
              chunk.forEach(([dsku, packages]) => {
                try {
                  const packageToRegister = packages.length ? packages : [createDefaultPackage(dsku, 1, 1)];

                  // Critical: Register field first to ensure it exists before any useFieldArray access
                  register(`${CreateShipmentInputName.SINGLE_SKU_PACKAGES}.${dsku}`);
                  // Then immediately set the value as an array to prevent "not iterable" errors
                  setValue(`${CreateShipmentInputName.SINGLE_SKU_PACKAGES}.${dsku}`, packageToRegister);
                } catch (error) {
                  log.error(`Failed to register packages for ${dsku}:`, error);
                  throw error;
                }
              });
            });
          };

          if (chunkIndex === 0) {
            // Process first chunk immediately
            processChunk();
          } else {
            // Process subsequent chunks with small delays to prevent UI blocking
            setTimeout(processChunk, chunkIndex * 10);
          }
        });
      } catch (error) {
        log.error("Failed to register packages:", error);
        throw error;
      }
    },
    [register, setValue, createDefaultPackage]
  );

  return {
    createCaseDimensionsFromBulkUpload,
    createBulkPackagesFromBulkUpload,
    registerBulkPackages,
  };
};
